import { Injectable, inject } from '@angular/core';
import { DateUtils, FlightType, SearcherState, SearcherStation } from '@vueling-ng/vy-cdk-searcher';
import { ProfessionalRoute } from 'app/core/common/models/professionals-route.config';
import { StateManagerService } from 'app/shared/state/state-manager.service';
import { QuickFilters } from 'app/views/booking-list/models/filters.model';
import { environment } from 'environments/environment';
import { SessionOidcService } from '../auth-oidc/session-oidc.service';
import {
  EmptyDigitalDataPage,
  EmptyDigitalDataUser,
  GaDigitalDataPage,
  GaDigitalDataUser,
  GaFlightType,
  GaItemId,
  InternalStatusBookingDigitalData,
  PageTrackingDigitalData,
  SearcherTrackingDigitalData
} from './models/analytics.config';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsDigitalService {
  private readonly _stateManagerService = inject(StateManagerService);
  private readonly _sessionOidcService = inject(SessionOidcService);

  public buildPageTrackingData(route: ProfessionalRoute, filter?: QuickFilters): PageTrackingDigitalData {
    return {
      page: this.loadPageDigitalData(route, filter),
      user: this.loadUserDigitalData()
    };
  }

  public buildSearcherTrackingData(searcherState: SearcherState): SearcherTrackingDigitalData {
    const isRoundTrip = searcherState.flightType === FlightType.RoundTrip;
    return {
      event: 'search_start',
      flight_type: isRoundTrip ? GaFlightType.RoundTrip : GaFlightType.OneWay,
      trip_duration: isRoundTrip
        ? DateUtils.differenceInDays(searcherState.outboundDate!, searcherState.returnDate!)
        : 1,
      item_id: GaItemId.Flight,
      country_route: this.buildCountryRoute(searcherState.selectedOrigin, searcherState.selectedDestination),
      pax_number:
        searcherState.pax.adultsQuantity +
        searcherState.pax.childrenQuantity +
        searcherState.pax.infantsQuantity +
        searcherState.pax.extraSeatsQuantity,
      adult_count: searcherState.pax.adultsQuantity,
      child_count: searcherState.pax.childrenQuantity,
      baby_count: searcherState.pax.infantsQuantity,
      extra_seat: searcherState.pax.extraSeatsQuantity,
      days_in_advance: DateUtils.differenceInDays(new Date(), searcherState.outboundDate!),
      departure_flight: `${searcherState.selectedOrigin.code}-${searcherState.selectedDestination.code}`,
      return_flight: isRoundTrip
        ? `${searcherState.selectedDestination.code}-${searcherState.selectedOrigin.code}`
        : undefined,
      departuredate: searcherState.outboundDate!.toISOString().split('T')[0],
      returndate: isRoundTrip ? searcherState.returnDate!.toISOString().split('T')[0] : undefined,
      departureoriginstationiata: searcherState.selectedOrigin.code,
      departurearrivalstationiata: searcherState.selectedDestination.code,
      returnoriginstationiata: isRoundTrip ? searcherState.selectedDestination.code : undefined,
      returnarrivalstationiata: isRoundTrip ? searcherState.selectedOrigin.code : undefined
    };
  }

  private loadPageDigitalData(route: ProfessionalRoute, filter?: QuickFilters): GaDigitalDataPage {
    const language = this._stateManagerService.currentState?.language;

    const pagename = !filter
      ? `${route.section}.${route.path}`
      : `${route.section}.${this.loadSectionByFilter(filter)}`;

    const subsection = !filter ? `${route.subSection}` : `${this.loadSectionByFilter(filter)}`;

    return {
      ...EmptyDigitalDataPage,
      language: `${language}`.split('-')[0].toLowerCase(),
      pagename: pagename.toLowerCase(),
      platformtype: InternalStatusBookingDigitalData.DESKTOP,
      section: `${route.section}`.toLowerCase(),
      subsection: subsection.toLowerCase(),
      technology: InternalStatusBookingDigitalData.SMART,
      requesttimestamp: new Date(),
      container: {
        id: `${environment.analytics.key}`.toLowerCase()
      },
      timeofday: `${new Date().getHours()}:${new Date().getMinutes()}`
    };
  }

  private loadSectionByFilter(filter: QuickFilters): string {
    return `${filter === 'FlownQuickFilters' ? InternalStatusBookingDigitalData.bookinglistflown : InternalStatusBookingDigitalData.bookingcancel}`;
  }

  private loadUserDigitalData(): GaDigitalDataUser {
    return {
      ...EmptyDigitalDataUser,
      clienttype: this._sessionOidcService.currentSession().userType?.toLowerCase() ?? '',
      geo: this._sessionOidcService.currentSessionOidc().userData.cultureCode ?? ''
    };
  }

  private buildCountryRoute(origin: SearcherStation, destination: SearcherStation): string {
    return `${origin.countryCode}-${destination.countryCode}`.toUpperCase();
  }
}
